import { Capacitor } from '@capacitor/core';
import { AccessControl, BiometricAuthError, BiometryType, NativeBiometric } from '@capgo/capacitor-native-biometric';

import { withPrivacyScreenSuspended } from '@/utils/privacyScreen';

/**
 * Thin wrapper around the native biometric plugin. The biometric credential lives in the
 * Keychain/Keystore under an access control that requires a live biometric match to read it, so a
 * successful read *is* the biometric check: there is no JS boolean an attacker could flip.
 *
 * Failures become discriminated results rather than throwing — "the user cancelled Face ID" is an
 * ordinary outcome. A binary installed before the plugin existed can still run this bundle under live
 * reload, so everything degrades to "unavailable" when the plugin is missing.
 */

const KEYCHAIN_SERVER = 'com.onthebuck.biometric';

export type BiometricKind = 'face' | 'touch' | 'fingerprint' | 'generic';

export type BiometricSupport = { available: true; kind: BiometricKind } | { available: false };

export type PromptText = {
	/** iOS shows this under the Face ID glyph; Android uses it as the prompt title. */
	reason: string;
	cancel: string;
};

export type ReadSecretResult =
	| { status: 'ok'; credentialId: string; secret: string }
	| { status: 'cancelled' }
	/** Nothing is stored, or the OS discarded it because the enrolled faces/fingers changed. */
	| { status: 'missing' }
	| { status: 'failed' };

export type StoreSecretResult = 'ok' | 'cancelled' | 'failed';

const CANCEL_CODES = new Set<number>([
	BiometricAuthError.USER_CANCEL,
	BiometricAuthError.APP_CANCEL,
	BiometricAuthError.SYSTEM_CANCEL,
	BiometricAuthError.USER_FALLBACK,
]);

let promptInFlight = false;

/** The OS prompt backgrounds the app on iOS and Android; the app lock must not count that as leaving. */
export function isBiometricPromptInFlight(): boolean {
	return promptInFlight;
}

async function withPrompt<T>(prompt: () => Promise<T>): Promise<T> {
	promptInFlight = true;
	try {
		return await withPrivacyScreenSuspended(prompt);
	} finally {
		promptInFlight = false;
	}
}

function errorCode(error: unknown): number | undefined {
	if (typeof error === 'object' && error !== null && 'code' in error) {
		const code = Number(error.code);
		return Number.isNaN(code) ? undefined : code;
	}
	return undefined;
}

function isCancel(error: unknown): boolean {
	const code = errorCode(error);
	return code !== undefined && CANCEL_CODES.has(code);
}

function toKind(type: BiometryType): BiometricKind {
	switch (type) {
		case BiometryType.FACE_ID:
			return 'face';
		case BiometryType.TOUCH_ID:
			return 'touch';
		case BiometryType.FINGERPRINT:
			return 'fingerprint';
		default:
			return 'generic';
	}
}

function isPluginAvailable(): boolean {
	return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('NativeBiometric');
}

/**
 * Only strong biometrics count: Android can only bind a Keystore key to Class 3 sensors, so a device
 * with nothing but weak face unlock could enroll but never read the credential back.
 */
export async function getBiometricSupport(): Promise<BiometricSupport> {
	if (!isPluginAvailable()) {
		return { available: false };
	}

	try {
		const result = await NativeBiometric.isAvailable({ useFallback: false });
		return result.isAvailable && result.strongBiometryIsAvailable
			? { available: true, kind: toKind(result.biometryType) }
			: { available: false };
	} catch {
		return { available: false };
	}
}

/**
 * iOS writes a biometry-protected item without prompting, so enrollment calls this first to prove the
 * person holding the phone is its owner. Android prompts inside `storeSecret` and does not need it.
 */
export async function verifyIdentity({ reason, cancel }: PromptText): Promise<boolean> {
	try {
		await withPrompt(() => NativeBiometric.verifyIdentity({ reason, title: reason, negativeButtonText: cancel }));
		return true;
	} catch {
		return false;
	}
}

/**
 * `BIOMETRY_CURRENT_SET` makes the OS discard the secret when someone enrolls a new face or finger,
 * so a person who learns the device passcode cannot add their own face and sign in as the owner.
 */
export async function storeSecret(
	credentialId: string,
	secret: string,
	{ reason, cancel }: PromptText,
): Promise<StoreSecretResult> {
	try {
		await withPrompt(() =>
			NativeBiometric.setCredentials({
				server: KEYCHAIN_SERVER,
				username: credentialId,
				password: secret,
				accessControl: AccessControl.BIOMETRY_CURRENT_SET,
				title: reason,
				negativeButtonText: cancel,
			}),
		);
		return 'ok';
	} catch (error) {
		return isCancel(error) ? 'cancelled' : 'failed';
	}
}

export async function readSecret({ reason, cancel }: PromptText): Promise<ReadSecretResult> {
	if (!isPluginAvailable()) {
		return { status: 'missing' };
	}

	try {
		const { username, password } = await withPrompt(() =>
			NativeBiometric.getSecureCredentials({
				server: KEYCHAIN_SERVER,
				reason,
				title: reason,
				negativeButtonText: cancel,
			}),
		);
		return { status: 'ok', credentialId: username, secret: password };
	} catch (error) {
		if (isCancel(error)) {
			return { status: 'cancelled' };
		}
		if (errorCode(error) === BiometricAuthError.NO_PROTECTED_CREDENTIALS_FOUND) {
			return { status: 'missing' };
		}
		return { status: 'failed' };
	}
}

export async function deleteSecret(): Promise<void> {
	if (!isPluginAvailable()) {
		return;
	}

	await NativeBiometric.deleteCredentials({ server: KEYCHAIN_SERVER }).catch(() => undefined);
}
